import { useRxCollection } from '@/providers/rxdb/rx-hooks';

import { useSmartDelete } from './use-smart-delete';
import { useSmartUpdate } from './use-smart-update';

import { buildArchivePatch } from '@flintmint/core-shared/rxdb';
import type { Block, Instruction, Note, UserSource } from '@flintmint/core-shared/rxdb';

import type { MutationOptions } from '@/hooks/rxdb-base';
import type { DocumentWithType } from '@/constants/types';

export const ARCHIVEABLE_DOCUMENT_TYPES = new Set(['notes', 'templates', 'blocks', 'userSources', 'instructions']);

export const isArchiveableDocument = (doc: Pick<DocumentWithType, 'type' | 'variant'>) =>
  ARCHIVEABLE_DOCUMENT_TYPES.has(doc.variant || doc.type);

export const useSmartArchive = () => {
  const { update } = useSmartUpdate();
  const { delete: smartDelete } = useSmartDelete();

  const notesCollection = useRxCollection<Note>('notes');
  const blocksCollection = useRxCollection<Block>('blocks');
  const userSourcesCollection = useRxCollection<UserSource>('userSources');
  const instructionsCollection = useRxCollection<Instruction>('instructions');

  const archive = async (documents: DocumentWithType[], mutationOptions?: MutationOptions) => {
    const patch = buildArchivePatch(true);
    for (const doc of documents.filter(isArchiveableDocument)) {
      await update(doc as any, patch, mutationOptions);
    }
  };

  const restore = async (documents: DocumentWithType[], mutationOptions?: MutationOptions) => {
    const patch = buildArchivePatch(false);
    for (const doc of documents.filter(isArchiveableDocument)) {
      await update(doc as any, patch, mutationOptions);
    }
  };

  // Queries RxDB directly (not the archive search/list), so pagination or filters never
  // hide docs from Empty Archive. Deletes one document at a time to avoid write storms.
  const empty = async (mutationOptions?: MutationOptions) => {
    const collections = [
      { collection: notesCollection, type: 'notes' as const },
      { collection: blocksCollection, type: 'blocks' as const },
      { collection: userSourcesCollection, type: 'userSources' as const },
      { collection: instructionsCollection, type: 'instructions' as const },
    ];

    for (const { collection, type } of collections) {
      if (!collection) continue;

      const docs = await collection.find({ selector: { archived: true } }).exec();
      for (const doc of docs) {
        await smartDelete([{ id: doc.id, name: '', type }], mutationOptions);
      }
    }
  };

  return { archive, restore, empty };
};
